// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/lib/homeEvents.ts
// 🔧 PURPOSE: Gli eventi come li racconta la Home, in funzioni pure:
//             - l'evento vicino ("Adesso per te": oggi, domani, dopodomani);
//             - gli eventi nelle date del soggiorno ("Nelle tue date");
//             - le etichette: "Stasera · 21:00", "Domani", "sab 3 ott ·
//               20:30" e il posto "Polignano a Mare · a 14 km".
//
//             L'ora di un evento si conosce solo se la pagina Eventi l'ha
//             già letta (cache `nva-event-times`, withCachedTimes): senza,
//             si scrive solo il giorno ("Oggi", "Domani"), mai un orario
//             inventato. Il tempo di viaggio non c'è in nessun dato: si
//             dice la distanza in km da Mola, che /api/events calcola.
//
//             "Oggi" è il giorno di Roma; il giorno di un evento si legge
//             dai campi locali della data, come in Esplora e nella pagina
//             Eventi (lib/featuredEvent).
// ============================================

import { withCachedTimes, type Event } from '../contexts/EventsContext';
import { eventDays, hasEventTime, happensBetween } from './featuredEvent';
import { romeDayKey, shiftDayKey } from './romeDay';
import { intlLocale } from './locale';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** "Stasera" dalle 18: prima è solo "Oggi · 10:00". */
const TONIGHT_FROM_HOUR = 18;

const startMs = (e: Pick<Event, 'startDate'>): number =>
  e.startDate instanceof Date ? e.startDate.getTime() : new Date(e.startDate).getTime();

/**
 * Il primo evento che comincia (o è ancora in corso) entro due giorni.
 * Spostato da SmartBookingCards così com'era (giorno del telefono, niente
 * orari dalla cache): la vecchia Home lo usa ancora.
 */
export const pickNearEvent = (events: Event[], now: Date): Event | null => {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const limit = start + 3 * 86400000; // oggi, domani, dopodomani
  const near = events
    .filter((e) => {
      const s = e.startDate instanceof Date ? e.startDate.getTime() : new Date(e.startDate).getTime();
      const end = e.endDate ? (e.endDate instanceof Date ? e.endDate.getTime() : new Date(e.endDate).getTime()) : s;
      if (isNaN(s)) return false;
      return s < limit && end >= start;
    })
    .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
  return near[0] || null;
};

/**
 * L'evento vicino della nuova Home: oggi, domani o dopodomani a Roma, con
 * l'ora quando la cache la conosce. Fra quelli dello stesso giorno vince
 * quello che comincia prima.
 */
export function nearEvent(events: Event[], now: Date = new Date()): Event | null {
  const today = romeDayKey(now);
  if (!today) return null;
  const last = shiftDayKey(today, 2);
  const list = withCachedTimes(events)
    .filter((e) => !isNaN(startMs(e)) && happensBetween(e, today, last))
    .sort((a, b) => startMs(a) - startMs(b));
  return list[0] || null;
}

/**
 * Gli eventi fra il giorno di arrivo e quello di partenza (chiavi
 * "AAAA-MM-GG", comprese), in ordine di inizio, al massimo `max`.
 * La lista degli eventi copre solo i prossimi 30 giorni: per un arrivo più
 * lontano torna vuota, e la Home dice "il programma esce più avanti".
 */
export function eventsInDates(events: Event[], from: string, to: string, max = 3): Event[] {
  if (!from || !to || to < from) return [];
  return withCachedTimes(events)
    .filter((e) => !isNaN(startMs(e)) && happensBetween(e, from, to))
    .sort((a, b) => startMs(a) - startMs(b))
    .slice(0, max);
}

export type EventWhenKind = 'tonight' | 'today' | 'tomorrow' | 'date';

export interface EventWhen {
  kind: EventWhenKind;
  /** Il giorno da scrivere ("AAAA-MM-GG"): il primo giorno dell'evento, o oggi se è in corso. */
  day: string;
  /** "21:00" se l'ora è nota, altrimenti null. */
  time: string | null;
}

const hhmm = (d: Date): string =>
  `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

/**
 * Quando, detto come sulla card:
 * - oggi con l'ora dalle 18 → "tonight"; oggi senza ora, o prima delle 18, o
 *   un evento di più giorni già cominciato → "today";
 * - domani → "tomorrow";
 * - più avanti → "date".
 */
export function describeEventWhen(event: Pick<Event, 'startDate' | 'endDate'>, now: Date = new Date()): EventWhen {
  const today = romeDayKey(now);
  const tomorrow = today ? shiftDayKey(today, 1) : '';
  const { start, end } = eventDays(event);
  const start0 = new Date(event.startDate);
  const time = hasEventTime(start0) ? hhmm(start0) : null;

  if (today && start <= today && end >= today) {
    // Cominciato nei giorni scorsi: l'ora era quella del primo giorno.
    if (start < today) return { kind: 'today', day: today, time: null };
    if (time && start0.getHours() >= TONIGHT_FROM_HOUR) return { kind: 'tonight', day: today, time };
    return { kind: 'today', day: today, time };
  }
  if (tomorrow && start === tomorrow) return { kind: 'tomorrow', day: start, time };
  return { kind: 'date', day: start, time };
}

/** "Stasera · 21:00", "Oggi", "Domani · 10:30", "sab 3 ott · 20:30" (maiuscole dal CSS). */
export function formatEventWhen(when: EventWhen, t: Translate, lang: string): string {
  let label: string;
  if (when.kind === 'tonight') label = t('home.now.tonight');
  else if (when.kind === 'today') label = t('home.now.todayLabel');
  else if (when.kind === 'tomorrow') label = t('home.now.tomorrow');
  else {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(when.day);
    const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
    try {
      label = d
        ? new Intl.DateTimeFormat(intlLocale(lang), { weekday: 'short', day: 'numeric', month: 'short' }).format(d)
        : when.day;
    } catch {
      label = when.day;
    }
  }
  return when.time ? `${label} · ${when.time}` : label;
}

/** Il posto: "Polignano a Mare · a 14 km"; a Mola (0 km) o senza distanza, solo il paese. */
export function eventPlace(event: Pick<Event, 'city' | 'distanceKm'>, t: Translate): string {
  const km = typeof event.distanceKm === 'number' && isFinite(event.distanceKm) ? Math.round(event.distanceKm) : null;
  if (km === null || km <= 0) return event.city;
  return `${event.city} · ${t('home.events.distanceKm', { km })}`;
}
